import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useOrders } from "@/api/commerce";
import { EmptyState, PageHeader, Spinner } from "@/components/ui";
import { money, shortDate } from "@/lib/format";
import { OrderStatusBadge, SyncBadge } from "./badges";

const TABS = [["open", "Open"], ["on_hold", "On hold"], ["", "All"], ["delivered", "Delivered"], ["cancelled", "Cancelled"]] as const;

export function OrdersPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("open");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 250); return () => clearTimeout(t); }, [search]);
  const orders = useOrders({ status: status || undefined, q: q || undefined });

  return (
    <>
      <PageHeader title="Sales orders" subtitle="Accepted quotes become orders. Schedule deliveries, dispatch in parts, and send them to your accounting system." />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input type="search" className="input max-w-xs" placeholder="Search order or PO number" aria-label="Search orders" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="inline-flex flex-wrap rounded-md border border-line bg-surface p-0.5" role="tablist" aria-label="Order status">
          {TABS.map(([value, label]) => (
            <button key={value} type="button" role="tab" aria-selected={status === value} onClick={() => setStatus(value)}
              className={`rounded px-3 py-1 text-sm font-medium ${status === value ? "bg-brand text-white" : "text-muted hover:text-ink"}`}>{label}</button>
          ))}
        </div>
      </div>
      {orders.isLoading && <Spinner />}
      {orders.data?.items.length === 0 && <EmptyState title="No orders here" body="Open an accepted quote and choose Create sales order." />}
      {!!orders.data?.items.length && (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-canvas text-xs uppercase tracking-wide text-muted">
              <tr><th className="px-4 py-3">Order</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Delivered</th><th className="px-4 py-3">Next due</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Accounts</th></tr>
            </thead>
            <tbody>
              {orders.data.items.map((o) => (
                <tr key={o.id} className="cursor-pointer border-b border-line last:border-0 hover:bg-canvas" onClick={() => navigate(`/orders/${o.id}`)}>
                  <td className="px-4 py-3"><Link to={`/orders/${o.id}`} className="font-medium hover:text-brand" onClick={(e) => e.stopPropagation()}>{o.number}</Link>
                    <span className="block text-xs text-muted">{shortDate(o.order_date)}{o.customer_po && ` · PO ${o.customer_po}`}</span></td>
                  <td className="px-4 py-3">{o.account?.name ?? <span className="text-muted">—</span>}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-medium tabular-nums">{money(o.grand_total, o.currency)}</td>
                  <td className="px-4 py-3"><Progress pct={o.delivered_pct} /></td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted">{o.next_due ? shortDate(o.next_due) : "—"}</td>
                  <td className="px-4 py-3"><OrderStatusBadge status={o.status} /></td>
                  <td className="px-4 py-3"><SyncBadge status={o.sync_status} target={o.sync_target} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function Progress({ pct }: { pct: number }) {
  return (
    <div className="flex items-center gap-2" aria-label={`${pct}% delivered`}>
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-canvas"><div className="h-full rounded-full bg-ok" style={{ width: `${Math.min(pct, 100)}%` }} /></div>
      <span className="text-xs tabular-nums text-muted">{Math.round(pct)}%</span>
    </div>
  );
}
